import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../i18n/useTranslation';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { Button } from '../components/atoms/Button/Button';
import { Card } from '../components/atoms/Card/Card';
import { EmptyState } from '../components/atoms/EmptyState';
import { Icon } from '../components/atoms/Icon';
import { Inline } from '../components/atoms/Inline/Inline';
import { Stack } from '../components/atoms/Stack/Stack';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { ManualMeasurementForm } from '../components/organisms/ManualMeasurementForm';
import { PhotoMeasurementCapture } from '../components/organisms/PhotoMeasurementCapture';
import { MeasurementExtractionReviewForm } from '../components/organisms/MeasurementExtractionReviewForm';
import type { PhotoExtractionResult } from '../services/measurementService';
import { PARAMETER_OPTIONS } from '../types/measurement';
import { useToast } from '../stores/toastStore';
import { Alert } from '../components/atoms/Alert/Alert';
import { Tabs } from '../components/atoms/Tabs';
import { MeasurementHistory } from '../components/measurements/MeasurementHistory';
import { LatestValuesTable } from '../components/measurements/LatestValuesTable';
import { useLatestParameters } from '../hooks/useLatestMeasurements';
import { LogTestsButton } from '../components/measurements/LogTestsButton';
import { useReturnFocus } from '../hooks/useReturnFocus';

type MeasurementView = 'values' | 'history' | 'entry';

const VIEWS: readonly MeasurementView[] = ['values', 'history', 'entry'];

export interface ManualMeasurementEntryPageProps {
  /**
   * Tab shown without a ?view= parameter. The sidebar's "Wassertests" opens the
   * values (/measurements); /measurements/new keeps opening the single-value form
   * for existing links (audit §4.2).
   */
  defaultView?: MeasurementView;
}

function ManualMeasurementEntryPage({ defaultView = 'entry' }: ManualMeasurementEntryPageProps = {}) {
  const { t } = useTranslation();
  const { aquariumId } = useParams<{ aquariumId: string }>();
  useReturnFocus();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  // entry → capture (photo upload + extraction) → review (edit + confirm) → entry
  const [isCaptureOpen, setIsCaptureOpen] = useState(false);
  const [extraction, setExtraction] = useState<PhotoExtractionResult | null>(null);
  // "Erfassen" | "Verlauf" (audit #14); ?view=history deep-links to the history.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get('view');
  const view: MeasurementView = VIEWS.find((v) => v === requestedView) ?? defaultView;
  const setView = (next: string): void => {
    const params = new URLSearchParams(searchParams);
    if (next === defaultView) params.delete('view');
    else params.set('view', next);
    setSearchParams(params, { replace: true });
  };

  // Latest value per parameter (1.5.0) — the same query as the dashboard tiles;
  // saving a measurement invalidates ['measurements'] and refreshes both.
  const latestQuery = useLatestParameters(aquariumId);
  const hasNoMeasurements = latestQuery.isSuccess && latestQuery.data.length === 0;

  const openCapture = () => {
    setExtraction(null);
    setIsCaptureOpen(true);
  };
  const closeCapture = () => setIsCaptureOpen(false);
  const handleExtracted = (result: PhotoExtractionResult) => {
    setExtraction(result);
    setIsCaptureOpen(false);
  };
  const discardExtraction = () => setExtraction(null);
  const handleConfirmed = () => {
    setExtraction(null);
    void queryClient.invalidateQueries({ queryKey: ['measurements'] });
    showToast(t('measurement.review.savedAnnouncement'), 'success');
  };

  if (extraction !== null && aquariumId !== undefined) {
    return (
      <AppLayout
        header={<Header />}
        sidebar={<Sidebar />}
        main={
          <PageContent width="narrow">
            <MeasurementExtractionReviewForm
              aquariumId={aquariumId}
              extraction={extraction}
              parameterOptions={PARAMETER_OPTIONS}
              onConfirmed={handleConfirmed}
              onRetakePhoto={openCapture}
              onCancel={discardExtraction}
            />
          </PageContent>
        }
      />
    );
  }

  if (isCaptureOpen && aquariumId !== undefined) {
    return (
      <AppLayout
        header={<Header />}
        sidebar={<Sidebar />}
        main={
          <PageContent width="narrow">
            <PageHeader
              title={t('measurement.capture.title')}
              description={t('measurement.capture.subtitle')}
            />
            <PhotoMeasurementCapture
              aquariumId={aquariumId}
              onExtracted={handleExtracted}
              onEnterManually={closeCapture}
              onCancel={closeCapture}
            />
          </PageContent>
        }
      />
    );
  }

  const entryPanel =
    aquariumId === undefined ? (
      <Alert tone="warning">{t('measurement.page.noAquarium')}</Alert>
    ) : (
      <Stack gap="8">
      <Section title={t('measurement.entry.sectionTitle')}>
        <Stack gap="6">
          {hasNoMeasurements && (
            <EmptyState
              icon={<Icon name="droplet" />}
              headline={t('measurement.entry.empty.title')}
              description={t('measurement.entry.empty.description')}
              actionLabel={t('measurement.entry.captureFromPhoto')}
              actionVariant="secondary"
              onAction={openCapture}
            />
          )}
          <Card>
            <Stack gap="4">
              {!hasNoMeasurements && (
                <Inline gap="2" justify="end" wrap>
                  <Button variant="secondary" onClick={openCapture}>
                    <Icon name="camera" size="sm" />
                    {t('measurement.entry.captureFromPhoto')}
                  </Button>
                </Inline>
              )}
              <ManualMeasurementForm aquariumId={aquariumId} />
            </Stack>
          </Card>
        </Stack>
      </Section>
      </Stack>
    );

  // The newest value per parameter — what "Wassertests" in the sidebar opens.
  const valuesPanel =
    aquariumId === undefined ? null : hasNoMeasurements ? (
      <EmptyState
        icon={<Icon name="droplet" />}
        headline={t('measurement.entry.empty.title')}
        description={t('measurement.entry.empty.description')}
        actionLabel={t('measurement.entry.captureFromPhoto')}
        actionVariant="secondary"
        onAction={openCapture}
      />
    ) : (
      <Section title={t('measurement.latest.title')}>
        <LatestValuesTable aquariumId={aquariumId} />
      </Section>
    );

  const pageTitle = {
    values: t('measurement.page.title'),
    history: t('measurement.history.title'),
    entry: t('measurement.entry.title'),
  }[view];
  const pageDescription = {
    values: t('measurement.page.subtitle'),
    history: t('measurement.history.subtitle'),
    entry: t('measurement.entry.subtitle'),
  }[view];

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width={view === 'entry' ? 'narrow' : 'default'}>
          <PageHeader
            title={pageTitle}
            description={pageDescription}
            actions={aquariumId ? <LogTestsButton aquariumId={aquariumId} id="measurements-log-tests" /> : undefined}
          />
          {aquariumId === undefined ? (
            entryPanel
          ) : (
            <Tabs
              aria-label={t('measurement.history.tabsLabel')}
              activeId={view}
              onChange={setView}
              items={[
                { id: 'values', label: t('measurement.latest.title'), panel: view === 'values' ? valuesPanel : null },
                {
                  id: 'history',
                  label: t('measurement.history.tabHistory'),
                  panel: view === 'history' ? (
                    <MeasurementHistory aquariumId={aquariumId} onAddMeasurement={() => setView('entry')} />
                  ) : null,
                },
                { id: 'entry', label: t('measurement.history.tabEntry'), panel: view === 'entry' ? entryPanel : null },
              ]}
            />
          )}
        </PageContent>
      }
    />
  );
}

export { ManualMeasurementEntryPage };
